import moment, { Moment } from 'moment-timezone';

export type TimeRange = { from: Moment; to: Moment };

export function getDefaultTimeRange(timeZone: string): TimeRange {
    const now = moment.tz(timeZone);
    const remainder = now.minute() % 15;
    const from = remainder === 0 ? now.clone().add(15, 'minutes') : now.clone().add(15 - remainder, 'minutes');
    from.second(0).millisecond(0);
    const to = from.clone().add(15, 'minutes');
    return {
        from: from.clone(),
        to: to.clone()
    };
}

export function getDefaultTimeRangesForRoundtrip(timeZone: string): { pickup: TimeRange; dropOff: TimeRange } {
    const pickup = getDefaultTimeRange(timeZone);
    const dropOffFrom = pickup.to.clone().add(1, 'hour');
    const dropOffTo = dropOffFrom.clone();
    return {
        pickup,
        dropOff: {
            from: dropOffFrom,
            to: dropOffTo
        }
    };
}
export function getDefaultTimeRangeFromOpening(openingMoment: Moment, timeZone: string): TimeRange {
    const from = openingMoment.clone().tz(timeZone).second(0).millisecond(0);
    const to = from.clone().add(1, 'hour');
    return { from, to };
}
export function getDefaultTimeRangesForRoundtripFromOpening(
    openingMoment: Moment,
    timeZone: string
): { pickup: TimeRange; dropOff: TimeRange } {
    const base = openingMoment.clone().tz(timeZone).second(0).millisecond(0);
    const pickup = {
        from: base.clone(),
        to: base.clone().add(1, 'hour')
    };
    const dropOff = {
        from: base.clone().add(3, 'hours'),
        to: base.clone().add(4, 'hours')
    };
    return { pickup, dropOff };
}

export function normalizeTimeRange(value: TimeRange, timeZone: string): TimeRange {
    const from = value.from.clone().tz(timeZone);
    let to = value.to.clone().tz(timeZone);
    if (from.isAfter(to)) {
        const toCandidate = from.clone().add(15, 'minutes');
        const endOfFromDay = from.clone().endOf('day');
        to = toCandidate.isAfter(endOfFromDay) ? from.clone() : toCandidate;
    }
    return { from, to };
}

export function clampRangeToMinTime(minTime: Moment, range: TimeRange, timeZone: string): TimeRange {
    const maxTime = minTime.clone().tz(timeZone);
    let from = range.from.clone().tz(timeZone);
    let to = range.to.clone().tz(timeZone);

    if (from.isBefore(maxTime)) {
        from = maxTime.clone();
        if (to.isBefore(from)) {
            to = from.clone();
        }
    }
    if (to.isBefore(maxTime)) {
        to = maxTime.clone();
        if (from.isAfter(to)) {
            from = to.clone();
        }
    }
    return { from, to };
}

export function applyPickupTimeChange(
    pickup: TimeRange,
    currentDropOff: TimeRange | undefined,
    timeZone: string
): { pickup: TimeRange; dropOff?: TimeRange } {
    const normalizedPickup = normalizeTimeRange(pickup, timeZone);
    const pickupMaxTime = normalizedPickup.to.clone();

    if (!currentDropOff) {
        return { pickup: normalizedPickup };
    }

    const dropOff = clampRangeToMinTime(pickupMaxTime, currentDropOff, timeZone);
    return {
        pickup: normalizedPickup,
        dropOff
    };
}

export function applyDropOffTimeChange(
    dropOff: TimeRange,
    pickupMaxTime: Moment | undefined,
    timeZone: string
): TimeRange {
    let result = normalizeTimeRange(dropOff, timeZone);

    if (pickupMaxTime) {
        const maxTime = pickupMaxTime.clone().tz(timeZone);
        if (result.from.isBefore(maxTime)) {
            result = clampRangeToMinTime(maxTime, result, timeZone);
        } else if (result.to.isBefore(maxTime)) {
            result = clampRangeToMinTime(maxTime, result, timeZone);
        }
    }
    return result;
}
